import { useMemo } from "react";
import { ChevronDown, ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";
import { motion, type Transition } from "framer-motion";
import type { Table } from "@tanstack/react-table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { fadeScaleIn } from "@/components/animation/presets";
import type { SortOption } from "@/config/sort-options/item";

type TranslationModule = Record<string, string>;

/** Fields that exist on every Frappe DocType — safe universal sort fallbacks */
const UNIVERSAL_SORT_OPTIONS: SortOption[] = [
  { field: "modified", label: "Last Updated On" },
  { field: "name", label: "ID" },
  { field: "creation", label: "Created On" },
];

/**
 * Derive sort options from the table's visible columns.
 * Falls back to universal options if no sortable columns are found.
 */
function deriveSortOptionsFromTable<TData>(table: Table<TData>): SortOption[] {
  const columns = table.getAllColumns().filter((col) => col.getCanSort());
  if (columns.length === 0) return UNIVERSAL_SORT_OPTIONS;

  const seen = new Set<string>();
  const options: SortOption[] = [];

  // Always lead with "modified" if available
  for (const col of columns) {
    if (col.id === "modified") {
      options.push({ field: col.id, label: (col.columnDef.header as string) || "Last Updated On" });
      seen.add(col.id);
      break;
    }
  }

  // Add remaining sortable columns
  for (const col of columns) {
    if (seen.has(col.id)) continue;
    const header = col.columnDef.header;
    const label = typeof header === "string" ? header : col.id;
    options.push({ field: col.id, label });
    seen.add(col.id);
  }

  // Ensure universal fields are present even if not in visible columns
  for (const fallback of UNIVERSAL_SORT_OPTIONS) {
    if (!seen.has(fallback.field)) {
      options.push(fallback);
    }
  }

  return options;
}

type SortSelectorProps<TData> = {
  table: Table<TData>;
  sortOptions?: SortOption[];
  translations?: TranslationModule;
  /** Compact mode — small inline button for ClickUp-style toolbar */
  compact?: boolean;
};

export function SortSelector<TData>({
  table,
  sortOptions,
  translations,
  compact = false,
}: SortSelectorProps<TData>) {
  const options = useMemo(
    () => sortOptions || deriveSortOptionsFromTable(table),
    [sortOptions, table]
  );
  const currentSort = table.getState().sorting[0];
  const currentField = currentSort?.id || "modified";
  const currentOrder = currentSort?.desc ? "desc" : "asc";

  const currentOption = options.find((opt) => opt.field === currentField) || options[0];

  const handleFieldSelect = (field: string) => {
    const newOrder = field === currentField && currentOrder === "desc" ? "asc" : "desc";
    table.setSorting([{ id: field, desc: newOrder === "desc" }]);
  };

  const handleOrderToggle = () => {
    if (!currentSort) return;
    const newOrder = currentOrder === "asc" ? "desc" : "asc";
    table.setSorting([{ id: currentField, desc: newOrder === "desc" }]);
  };

  if (compact) {
    return (
      <div className="flex items-center">
        <button
          type="button"
          onClick={handleOrderToggle}
          className="flex items-center justify-center h-8 w-8 rounded-xs text-muted-foreground hover:text-foreground hover:bg-accent/60 transition-colors"
          title={currentOrder === "desc" ? (translations?.descending || "Descending") : (translations?.ascending || "Ascending")}
          disabled={!currentSort}
          aria-label={currentOrder === "desc" ? "Sort descending" : "Sort ascending"}
        >
          {currentOrder === "desc" ? (
            <ArrowDown className="w-4 h-4" />
          ) : (
            <ArrowUp className="w-4 h-4" />
          )}
        </button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="flex items-center gap-1.5 h-8 px-2.5 rounded-xs text-sm text-muted-foreground hover:text-foreground hover:bg-accent/60 transition-colors whitespace-nowrap"
              aria-label={translations?.sortBy || "Sort by"}
            >
              <ArrowUpDown className="w-4 h-4" />
              <span className="max-w-[140px] truncate">{currentOption.label}</span>
              <ChevronDown className="w-3.5 h-3.5 opacity-50 shrink-0" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-[200px]">
            {options.map((option) => (
              <DropdownMenuItem
                key={option.field}
                onClick={() => handleFieldSelect(option.field)}
                className={cn(
                  "cursor-pointer text-sm",
                  currentField === option.field && "bg-accent/70 dark:bg-accent/50"
                )}
              >
                {option.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  }

  return (
    <div className="flex items-center rounded-full bg-white dark:bg-gray-900 border border-border overflow-hidden">
      <button
        type="button"
        onClick={handleOrderToggle}
        className="flex items-center justify-center rounded-none rounded-l-full hover:scale-105 active:scale-95 transition-all duration-300 ease-out hover:bg-white/40 dark:hover:bg-gray-800/40 h-14 w-14"
        title={currentOrder === "desc" ? (translations?.descending || "Descending") : (translations?.ascending || "Ascending")}
        disabled={!currentSort}
        aria-label={currentOrder === "desc" ? "Sort descending" : "Sort ascending"}
      >
        {currentOrder === "desc" ? (
          <ArrowDown className="w-5 h-5" />
        ) : (
          <ArrowUp className="w-5 h-5" />
        )}
      </button>
      <div className="h-6 w-px bg-border" />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="flex items-center rounded-none rounded-r-full hover:scale-105 active:scale-95 transition-all duration-300 ease-out hover:bg-white/40 dark:hover:bg-gray-800/40 h-14 px-5 text-base font-normal justify-between min-w-[180px]"
            aria-label={translations?.sortBy || "Sort by"}
          >
            <div className="flex items-center gap-2">
              <ArrowUpDown className="w-5 h-5" />
              <span>{currentOption.label}</span>
            </div>
            <ChevronDown className="ml-2 w-5 h-5 shrink-0 opacity-50" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-[200px] p-0" asChild>
          <motion.div
            initial={fadeScaleIn.initial}
            animate={fadeScaleIn.animate}
            exit={fadeScaleIn.exit}
            transition={fadeScaleIn.transition as Transition}
            className="w-full rounded-2xl overflow-hidden"
          >
            {options.map((option) => (
              <DropdownMenuItem
                key={option.field}
                onClick={() => handleFieldSelect(option.field)}
                className={cn(
                  "cursor-pointer",
                  currentField === option.field && "bg-accent/70 dark:bg-accent/50"
                )}
              >
                {option.label}
              </DropdownMenuItem>
            ))}
          </motion.div>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
